/**
 * Initials for avatar (2 chars), same rules as workspace list cards.
 *
 * @param {string | null | undefined} name
 * @returns {string}
 */
export const displayInitials = (name) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
};

/**
 * Maps GET /v1/workspace/:id `body` to a stable UI shape (camelCase + snake_case fallbacks).
 *
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {{
 *   workspaceName: string,
 *   slug: string,
 *   status: string,
 *   isActive: boolean,
 *   address: string,
 *   photoUrl: string | null,
 *   createdAt: string | null,
 *   ownerName: string,
 *   ownerEmail: string,
 *   phoneNumber: string,
 *   userActiveCount: number,
 *   userCount: number,
 *   agents: number,
 *   workflows: number,
 *   campaigns: number,
 * } | null}
 */
export const mapWorkspaceOverviewBody = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const workspaceName =
    (typeof raw.workspaceName === 'string' && raw.workspaceName) ||
    (typeof raw.workspace_name === 'string' && raw.workspace_name) ||
    '';

  const slug =
    (typeof raw.slug === 'string' && raw.slug.trim()) ||
    (typeof raw.workspaceSlug === 'string' && raw.workspaceSlug.trim()) ||
    (typeof raw.workspace_slug === 'string' && raw.workspace_slug.trim()) ||
    '';

  const status = (typeof raw.status === 'string' && raw.status) || '';
  const isActive = Boolean(raw.isActive ?? raw.is_active);

  const address =
    (typeof raw.address === 'string' && raw.address) ||
    (typeof raw.location === 'string' && raw.location) ||
    '';

  const photoUrl =
    (typeof raw.photoUrl === 'string' && raw.photoUrl) ||
    (typeof raw.photo_url === 'string' && raw.photo_url) ||
    null;

  const createdAt =
    (typeof raw.createdAt === 'string' && raw.createdAt) ||
    (typeof raw.created_at === 'string' && raw.created_at) ||
    null;

  const ownerName =
    (typeof raw.ownerName === 'string' && raw.ownerName) ||
    (typeof raw.owner_name === 'string' && raw.owner_name) ||
    '';

  const ownerEmail =
    (typeof raw.ownerEmail === 'string' && raw.ownerEmail) ||
    (typeof raw.owner_email === 'string' && raw.owner_email) ||
    '';

  const phoneNumber =
    (typeof raw.phoneNumber === 'string' && raw.phoneNumber) ||
    (typeof raw.phone_number === 'string' && raw.phone_number) ||
    (typeof raw.phone === 'string' && raw.phone) ||
    '';

  return {
    workspaceName,
    slug,
    status,
    isActive,
    address,
    photoUrl,
    createdAt,
    ownerName,
    ownerEmail,
    phoneNumber,
    userActiveCount: Number(raw.userActiveCount ?? raw.user_active_count ?? 0) || 0,
    userCount: Number(raw.userCount ?? raw.user_count ?? 0) || 0,
    agents: Number(raw.agents ?? 0) || 0,
    workflows: Number(raw.workflows ?? 0) || 0,
    campaigns: Number(raw.campaigns ?? 0) || 0,
  };
};

/**
 * Maps overview/detail (GET /v1/workspace/:id) into the same list-row shape as
 * {@link mapWorkspaceListItemToUi} so detail layout can render when the workspace
 * is not on the current paginated list response (deep link, other page, filters).
 *
 * @param {string} workspaceId
 * @param {object} detail - normalized overview object from {@link mapWorkspaceOverviewBody}
 */
export const mapWorkspaceOverviewToListItem = (workspaceId, detail) => {
  if (!workspaceId?.trim() || !detail) return null;
  return {
    id: workspaceId.trim(),
    name: detail.workspaceName || '',
    status: detail.status,
    isActive: detail.isActive,
    profilePhotoUrl: detail.photoUrl ?? '',
    ownerName: detail.ownerName,
    membersCount: detail.userCount,
    createdAt: detail.createdAt,
    updatedAt: null,
  };
};
